import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../hooks/useRole';
import { confirmNavigation } from '../hooks/useUnsavedChangesWarning';
import { ThemedText, FAQModal, Avatar, DevAccountSwitcher } from './';
import { BugReportModal } from './BugReportModal';
import { DEV_TOOLS_ENABLED } from '../utils/devTools';
import { recordNavigation } from '../utils/bugReporterContext';
import { formSchemaService } from '../api';
import { extractItems } from '../utils/responseUtils';
import { renderFormIcon } from '../utils/iconUtils';
import {
  LayoutDashboard, Building2, Ticket, ClipboardList, KeyRound,
  Mail, MessageSquare, BadgeHelp, FileText, Settings, FileStack,
  ClipboardCheck, Calendar, User, MapPin,
  Pin, PinOff, Menu, ChevronDown, AlertTriangle,
  LogOut, FlaskConical, HardDrive, Microscope, Bug,
  Database, MonitorPlay, Eye,
} from 'lucide-react';

// ─── Data Helpers ─────────────────────────────────────────────────

const getSidebarSections = (isSuperAdmin, isAdmin, user) => {
  // SuperAdmin gets a full sidebar with all admin tools (no gear menu needed)
  if (isSuperAdmin) {
    return [
      {
        id: 'main',
        label: 'MAIN',
        collapsible: false,
        items: [
          { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, path: '/dashboard' },
          { id: 'organizations', label: 'Organizations', icon: <Building2 size={18} />, path: '/organizations' },
          { id: 'demo-studio', label: 'Demo Studio', icon: <MonitorPlay size={18} />, path: '/demo-studio' },
        ],
      },
      {
        id: 'operations',
        label: 'OPERATIONS',
        collapsible: true,
        items: [
          { id: 'tasks', label: 'Tasks', icon: <Ticket size={18} />, path: '/tasks' },
          { id: 'lab-reports', label: 'Lab Reports', icon: <FlaskConical size={18} />, path: '/lab-reports' },
        ],
      },
      {
        id: 'licensing',
        label: 'LICENSING',
        collapsible: true,
        items: [
          { id: 'license-plans', label: 'License Plans', icon: <ClipboardList size={18} />, path: '/license-plans' },
          { id: 'license-management', label: 'Licenses', icon: <KeyRound size={18} />, path: '/license-management' },
        ],
      },
      {
        id: 'communications',
        label: 'COMMUNICATIONS',
        collapsible: true,
        items: [
          { id: 'email-templates', label: 'Email Templates', icon: <Mail size={18} />, path: '/email-templates' },
          { id: 'sms-templates', label: 'SMS Templates', icon: <MessageSquare size={18} />, path: '/sms-templates' },
        ],
      },
      {
        id: 'tools',
        label: 'TOOLS',
        collapsible: true,
        items: [
          { id: 'faqs', label: 'Manage FAQs', icon: <MessageSquare size={18} />, path: '/faqs' },
          { id: 'generated-reports', label: 'Reports', icon: <FileText size={18} />, path: '/generated-reports' },
          { id: 'database-backups', label: 'DB Backups', icon: <HardDrive size={18} />, path: '/database-backups' },
          { id: 'lab-formats', label: 'Lab Formats', icon: <Microscope size={18} />, path: '/lab-formats' },
          { id: 'developer-tools', label: 'Developer Tools', icon: <Settings size={18} />, path: '/developer-tools' },
        ],
      },
    ];
  }

  // OrgAdmin / User sidebar
  const operationsItems = [
    { id: 'work-orders', label: 'Work Orders', icon: <ClipboardCheck size={18} />, path: '/work-orders' },
  ];

  if (user?.isInternalTaskOrganization) {
    operationsItems.push({ id: 'tasks', label: 'Tasks', icon: <Ticket size={18} />, path: '/tasks' });
  }

  if (isAdmin) {
    operationsItems.push({ id: 'lab-reports', label: 'Lab Reports', icon: <FlaskConical size={18} />, path: '/lab-reports' });
  }

  operationsItems.push(
    { id: 'calendar', label: 'Calendar', icon: <Calendar size={18} />, path: '/calendar' },
    { id: 'customers', label: 'Customers', icon: <User size={18} />, path: '/customers' },
    { id: 'locations', label: 'Locations', icon: <MapPin size={18} />, path: '/locations' },
  );

  const sections = [
    {
      id: 'main',
      label: 'MAIN',
      collapsible: false,
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, path: '/dashboard' },
        { id: 'forms', label: 'Forms', icon: <FileStack size={18} />, path: '/forms' },
        { id: 'knowledge-base', label: 'Knowledge Base', icon: <Database size={18} />, path: '/knowledge-base' },
      ],
    },
    {
      id: 'operations',
      label: 'OPERATIONS',
      collapsible: true,
      items: operationsItems,
    },
  ];

  return sections;
};

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/forms': 'Forms',
  '/form-builder': 'Form Builder',
  '/tasks': 'Operations',
  '/tasks/new': 'Operations',
  '/work-orders': 'Operations',
  '/work-orders/new': 'Operations',
  '/calendar': 'Operations',
  '/customers': 'Operations',
  '/locations': 'Operations',
  '/users': 'Users',
  '/faqs': 'Manage FAQs',
  '/faqs/new': 'Create FAQ',
  '/help': 'Help Center',
  '/organizations': 'Organizations',
  '/demo-studio': 'Demo Studio',
  '/demo-studio/new': 'New Demo',
  '/license-plans': 'License Plans',
  '/license-management': 'Licenses',
  '/license-status': 'License & Usage',
  '/notification-events': 'Notification Events',
  '/email-templates': 'Email Templates',
  '/sms-templates': 'SMS Templates',
  '/developer-tools': 'Developer Tools',
  '/profile': 'Profile',
  '/settings': 'Settings',
  '/generated-reports': 'Reports',
  '/file-manager': 'File Manager',
  '/hidden-forms': 'Hidden Forms',
  '/my-certificates': 'Operations',
  '/certificates': 'Certificate Types',
  '/webhooks': 'Webhooks',
  '/webhooks/outbound/new': 'New Outbound Endpoint',
  '/webhooks/inbound/new': 'New Inbound Endpoint',
  '/audit-log': 'Audit Log',
  '/lab-reports': 'Lab Reports',
  '/lab-reports/upload': 'Upload Lab Report',
  '/lab-report-templates': 'Lab Report Templates',
  '/ai-usage': 'AI Usage',
  '/database-backups': 'Database Backups',
  '/lab-formats': 'Lab Document Formats',
  '/knowledge-base': 'Knowledge Base',
};

const getPageTitle = (path) => {
  if (PAGE_TITLES[path]) return PAGE_TITLES[path];
  if (path.startsWith('/organizations/') && path !== '/organizations') return 'Organization Detail';
  if (path.startsWith('/demo-studio/') && path !== '/demo-studio/new') return 'Edit Demo';
  if (path.startsWith('/forms/') && path.includes('/submissions/create')) return 'New Submission';
  if (path.startsWith('/forms/') && path.includes('/submissions/')) return 'Submission Detail';
  if (path.startsWith('/forms/') && path.includes('/submissions')) return 'My Forms';
  if (path.startsWith('/forms/') && path.includes('/pdf-templates/designer')) return 'Report Template Designer';
  if (path.startsWith('/forms/') && path.includes('/pdf-templates/test')) return 'Report Template Test';
  if (path.startsWith('/forms/') && path.includes('/pdf-templates')) return 'Report Templates';
  if (path.startsWith('/forms/') && path.includes('/image-report')) return 'Image Report Config';
  if (path.startsWith('/tasks/') && path.includes('/edit')) return 'Edit Task';
  if (path.startsWith('/tasks/')) return 'Task Detail';
  if (path.startsWith('/sentry-issues/')) return 'Sentry Issue Detail';
  if (path.startsWith('/faqs/') && path.includes('/edit')) return 'Edit FAQ';
  if (path.startsWith('/work-orders/') && path.includes('/edit')) return 'Edit Work Order';
  if (path.startsWith('/work-orders/')) return 'Work Order Detail';
  if (path.startsWith('/form-builder/')) return 'Edit Form';
  if (path.startsWith('/email-templates/designer')) return 'Email Template Designer';
  if (path.startsWith('/license-management/')) return 'License Detail';
  if (path.startsWith('/webhooks/outbound/') && path.includes('/edit')) return 'Edit Outbound Endpoint';
  if (path.startsWith('/webhooks/inbound/') && path.includes('/edit')) return 'Edit Inbound Endpoint';
  if (path.startsWith('/lab-formats/calibrate')) return 'Calibrate Format';
  if (path.startsWith('/lab-formats/') && !path.includes('calibrate')) return 'Format Detail';
  if (path.startsWith('/lab-reports/')) return 'Lab Report Detail';
  return '';
};

const COLLAPSED_SECTIONS_KEY = 'sidebar-collapsed-sections';

const DEFAULT_COLLAPSED = {
  operations: false,
  licensing: false,
  communications: false,
  tools: false,
  management: false,
  'my-forms': false,
};

const loadCollapsedSections = () => {
  try {
    const stored = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
    return stored ? JSON.parse(stored) : DEFAULT_COLLAPSED;
  } catch {
    return DEFAULT_COLLAPSED;
  }
};

const saveCollapsedSections = (sections) => {
  try {
    localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(sections));
  } catch { /* ignore */ }
};

// ─── Animations ──────────────────────────────────────────────────

const dropdownSlide = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pageEnter = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const sidebarGradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const orbFloat = keyframes`
  0%   { transform: translate(0px, 0px) scale(1); }
  33%  { transform: translate(12px, -18px) scale(1.08); }
  66%  { transform: translate(-8px, -10px) scale(0.94); }
  100% { transform: translate(0px, 0px) scale(1); }
`;

// ─── Styled Components ────────────────────────────────────────────

const LayoutContainer = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: ${props => props.theme.colors.background};
`;

/* Animated background layer — gradient + floating orbs */
const SidebarBg = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;

  /* Animated gradient — explicit properties so animation fires correctly */
  background-image: linear-gradient(
    135deg,
    #04060f 0%,
    #0a0d25 18%,
    #121540 36%,
    #1e2260 52%,
    #2a2e82 64%,
    #1a1f5a 78%,
    #0a0d25 100%
  );
  background-size: 300% 300%;
  background-position: 0% 50%;
  animation: ${sidebarGradientShift} 9s ease infinite;

  /* Large teal orb — top area */
  &::before {
    content: '';
    position: absolute;
    top: -80px;
    right: -70px;
    width: 300px;
    height: 300px;
    background: radial-gradient(
      circle at 38% 38%,
      rgba(89, 202, 238, 0.26) 0%,
      rgba(89, 202, 238, 0.08) 45%,
      transparent 68%
    );
    animation: ${orbFloat} 10s ease-in-out infinite;
  }

  /* Second orb — bottom left */
  &::after {
    content: '';
    position: absolute;
    bottom: 40px;
    left: -80px;
    width: 260px;
    height: 260px;
    background: radial-gradient(
      circle at 60% 60%,
      rgba(89, 202, 238, 0.16) 0%,
      rgba(42, 46, 130, 0.1) 50%,
      transparent 70%
    );
    animation: ${orbFloat} 13s ease-in-out infinite reverse;
    animation-delay: -5s;
  }
`;

const Sidebar = styled.div`
  width: ${props => props.$isExpanded ? '240px' : '56px'};
  background: ${props => props.theme.colors.sidebar?.background || '#232562'};
  border-right: none;
  transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.35s ease;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  z-index: 1000;
  box-shadow: 4px 0 24px rgba(35, 37, 98, 0.18);
  overflow: hidden;

  /* Hidden off-screen with hamburger toggle (unless pinned) */
  @media (max-width: 1700px) {
    ${props => props.$isPinned ? '' : `
      transform: translateX(${props.$isMobileOpen ? '0' : '-100%'});
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    `}
    width: 240px;
  }
`;

const SidebarHeader = styled.div`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  min-height: 56px;
  flex-shrink: 0;
  overflow: hidden;
  white-space: nowrap;
  position: relative;
  z-index: 1;
`;

const PinButton = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.sm};
  color: ${props => props.$pinned ? props.theme.colors.sidebar.textActive : props.theme.colors.sidebar.text};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s ease;
  opacity: ${props => props.$visible ? 1 : 0};

  &:hover {
    background-color: rgba(89, 202, 238, 0.12);
    border-color: rgba(89, 202, 238, 0.3);
    color: ${props => props.theme.colors.sidebar.textActive};
  }
`;

const LogoImage = styled.img`
  height: 28px;
  width: auto;
  flex-shrink: 0;
`;

const LogoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
  border-radius: 10px;
  padding: ${props => props.$isExpanded ? '8px 14px' : '6px'};
  flex: ${props => props.$isExpanded ? 1 : 'none'};
  min-width: 0;
  transition: padding 0.2s ease, flex 0.2s ease;
`;

const LogoText = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #232562;
  white-space: nowrap;
  letter-spacing: -0.2px;
`;

const SidebarTitle = styled.div`
  padding: 28px 16px 8px;
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.2px;
  flex-shrink: 0;
`;

const SidebarNav = styled.nav`
  flex: 1;
  padding: ${props => props.theme.spacing.sm} 0;
  overflow-y: auto;
  position: relative;
  z-index: 1;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.sidebar.border};
    border-radius: 4px;
  }
`;

const SectionHeader = styled.button`
  width: calc(100% - 24px);
  margin: 2px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: none;
  border: none;
  color: ${props => props.theme.colors.sidebar.text};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  cursor: pointer;
  transition: color 0.2s ease;
  user-select: none;

  &:hover {
    color: ${props => props.theme.colors.sidebar.textActive};
  }
`;

const SectionChevron = styled.span`
  font-size: 10px;
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: rotate(${props => props.$isOpen ? '0deg' : '-90deg'});
  opacity: 0.6;
`;

const SectionDividerCollapsed = styled.div`
  height: 1px;
  background-color: rgba(255, 255, 255, 0.07);
  margin: 8px 8px;
`;

const NavItem = styled.button`
  width: calc(100% - 8px);
  margin: 2px 4px;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: 10px;
  background: none;
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.$active ? props.theme.colors.sidebar.textActive : props.theme.colors.sidebar.text};
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: ${props => props.$active ? '600' : '400'};
  position: relative;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    background-color: ${props => props.theme.colors.sidebar.hover};
    color: ${props => props.theme.colors.sidebar.textActive};
    border-color: ${props => props.theme.colors.sidebar.border};
  }

  ${props => props.$active && `
    background: linear-gradient(90deg, ${props.theme.colors.sidebar.hover}, transparent);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      height: 20px;
      width: 3px;
      background: linear-gradient(180deg, ${props.theme.colors.tint}, ${props.theme.colors.info});
      border-radius: 0 4px 4px 0;
      box-shadow: 0 0 8px ${props.theme.colors.tint}40;
    }
  `}

  ${props => props.$disabled && `
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const NavIcon = styled.span`
  font-size: 18px;
  width: 24px;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-shrink: 0;
`;

const NavLabel = styled.span`
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease;
  overflow: hidden;
  white-space: nowrap;
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: ${props => props.$sidebarWidth};
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  transition: margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 1700px) {
    margin-left: ${props => props.$isPinned ? props.$sidebarWidth : '0'};
  }
`;

const ReadOnlyBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  padding: 6px ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.tint}18;
  border-bottom: 1px solid ${props => props.theme.colors.tint}40;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  flex-shrink: 0;
`;

const TopBar = styled.div`
  height: 64px;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${props => props.theme.spacing.lg};
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(35, 37, 98, 0.06);
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const TopBarLogo = styled.img`
  height: 30px;
  width: auto;
  flex-shrink: 0;
`;

const PageTitle = styled.h1`
  font-size: ${props => props.theme.typography.sizes.medium};
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0;
  white-space: nowrap;

  @media (max-width: 480px) {
    display: none;
  }
`;

const DemoBadge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background-color: ${props => props.theme.colors.tint}18;
  border: 1px solid ${props => props.theme.colors.tint}40;
  color: ${props => props.theme.colors.tint};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
  user-select: none;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  font-size: 20px;
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};

  @media (max-width: 1700px) {
    display: block;
  }
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const TopBarIconButton = styled.button`
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$active ? props.theme.colors.tint + '18' : 'none'};
  border: 1px solid ${props => props.$active ? props.theme.colors.tint + '40' : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.round};
  color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
  position: relative;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    border-color: ${props => props.theme.colors.tint};
    color: ${props => props.theme.colors.tint};
  }
`;

const UserAvatarContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #59caee, #232562);
  border-radius: ${props => props.theme.borderRadius.round};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(89, 202, 238, 0.25);
`;

const ProfileDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${props => props.theme.spacing.xs};
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: 0 12px 40px rgba(35, 37, 98, 0.15), 0 4px 12px rgba(35, 37, 98, 0.08);
  min-width: 220px;
  z-index: 1001;
  overflow: hidden;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  animation: ${props => props.$isOpen ? dropdownSlide : 'none'} 0.2s ease-out;
`;

const DropdownUserHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: 10px ${props => props.theme.spacing.lg};
  background: ${props => props.$active ? props.theme.colors.tint + '12' : 'none'};
  border: none;
  color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.text};
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: ${props => props.theme.typography.sizes.default};

  &:hover {
    background-color: ${props => props.theme.colors.tint}08;
    padding-left: 28px;
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background-color: ${props => props.theme.colors.border};
  margin: ${props => props.theme.spacing.xs} 0;
`;

const ContentArea = styled.div`
  flex: 1;
  padding: ${props => props.theme.spacing.lg};
  overflow-y: auto;
  animation: ${pageEnter} 0.15s ease-out;
`;

const MobileOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 18, 41, 0.6);
  backdrop-filter: blur(4px);
  z-index: 999;
  display: ${props => props.$show ? 'block' : 'none'};
  animation: ${props => props.$show ? fadeIn : 'none'} 0.2s ease-out;

  @media (min-width: 1851px) {
    display: none;
  }
`;



// ─── Component ────────────────────────────────────────────────────

const SIDEBAR_PINNED_KEY = 'sidebar-pinned';

const loadIsPinned = () => {
  try {
    return localStorage.getItem(SIDEBAR_PINNED_KEY) === 'true';
  } catch {
    return false;
  }
};

const useIsTablet = () => {
  const [isTablet, setIsTablet] = useState(
    () => window.innerWidth <= 1850 && window.innerWidth > 768
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1700px) and (min-width: 769px)');
    const handler = (e) => setIsTablet(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isTablet;
};

export const Layout = ({ children, currentPath = '/dashboard' }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [isBugModalOpen, setIsBugModalOpen] = useState(false);
  const [dynamicForms, setDynamicForms] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState(loadCollapsedSections);
  const theme = useTheme();
  const { user, logout } = useAuth();
  const { isSuperAdmin, isAdmin } = useRole();
  const navigate = useNavigate();
  const location = useLocation();

  const [isPinned, setIsPinned] = useState(loadIsPinned);
  const isExpanded = true;

  // QA bug-reporter: keep a short tail of recent routes so the report can include them.
  useEffect(() => {
    if (DEV_TOOLS_ENABLED) recordNavigation(window.location.href);
  }, [location.pathname]);

  // Fetch active forms for sidebar menu
  useEffect(() => {
    const fetchForms = async () => {
      try {
        const response = await formSchemaService.getFormSchemas({
          includeInactive: false,
          pageSize: 10,
        });
        const formsData = extractItems(response);
        const formMenuItems = formsData.map(form => ({
          id: `form-${form.id}`,
          label: form.name,
          icon: form.schemaDefinition?.icon || form.icon || 'clipboard-list',
          path: `/forms/${form.id}/submissions`,
          formId: form.id,
        }));
        setDynamicForms(formMenuItems);
      } catch (error) {
        console.error('Failed to fetch forms for menu:', error);
      }
    };
    fetchForms();
  }, []);

  const sidebarSections = getSidebarSections(isSuperAdmin(), isAdmin(), user);
  const pageTitle = getPageTitle(currentPath);

  const handleTogglePin = useCallback(() => {
    setIsPinned(prev => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_PINNED_KEY, String(next));
      return next;
    });
  }, []);

  const toggleSection = useCallback((sectionId) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      saveCollapsedSections(next);
      return next;
    });
  }, []);

  const handleNavigation = (path) => {
    if (!confirmNavigation()) return;
    navigate(path);
    setIsMobileSidebarOpen(false);
    setIsProfileMenuOpen(false);
  };

  const handleProfileMenuToggle = () => {
    setIsProfileMenuOpen(prev => !prev);
  };

  const handleProfileClick = () => {
    if (!confirmNavigation()) return;
    navigate('/settings');
    setIsProfileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsProfileMenuOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileMenuOpen && !event.target.closest('[data-profile-menu]')) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen]);

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const sidebarWidth = '240px';

  // Check if an item in a section is active (for auto-expanding sections with active route)
  const isSectionActive = (section) => {
    return section.items.some(item => currentPath === item.path || currentPath.startsWith(item.path + '/'));
  };

  const isSectionOpen = (section) => {
    if (!section.collapsible) return true;
    // If section has the active item, always show it
    if (isSectionActive(section)) return true;
    return !collapsedSections[section.id];
  };

  const renderSidebarSection = (section) => {
    const isOpen = isSectionOpen(section);

    return (
      <React.Fragment key={section.id}>
        {/* Section header - show divider line when sidebar not expanded */}
        {!isExpanded ? (
          section.collapsible && <SectionDividerCollapsed theme={theme} />
        ) : (
          section.collapsible && (
            <SectionHeader
              theme={theme}
              onClick={() => toggleSection(section.id)}
              title={isOpen ? `Collapse ${section.label}` : `Expand ${section.label}`}
            >
              <span>{section.label}</span>
              <SectionChevron $isOpen={isOpen}><ChevronDown size={14} /></SectionChevron>
            </SectionHeader>
          )
        )}

        {/* Section items */}
        {(isOpen || !isExpanded) && section.items.map(item => (
          <NavItem
            key={item.id}
            theme={theme}
            $active={currentPath === item.path || currentPath.startsWith(item.path + '/')}
            $disabled={item.disabled}
            onClick={() => !item.disabled && handleNavigation(item.path)}
            title={item.disabled ? `${item.label} (coming soon)` : item.label}
          >
            <NavIcon>{item.icon}</NavIcon>
            <NavLabel $visible={isExpanded}>{item.label}</NavLabel>
          </NavItem>
        ))}
      </React.Fragment>
    );
  };

  const renderDynamicFormsSection = () => {
    if (dynamicForms.length === 0) return null;

    const formsSection = {
      id: 'my-forms',
      label: 'MY FORMS',
      collapsible: true,
    };
    const isOpen = !collapsedSections[formsSection.id];

    return (
      <>
        {!isExpanded ? (
          <SectionDividerCollapsed theme={theme} />
        ) : (
          <SectionHeader
            theme={theme}
            onClick={() => toggleSection(formsSection.id)}
            title={isOpen ? 'Collapse MY FORMS' : 'Expand MY FORMS'}
          >
            <span>MY FORMS</span>
            <SectionChevron $isOpen={isOpen}><ChevronDown size={14} /></SectionChevron>
          </SectionHeader>
        )}

        {(isOpen || !isExpanded) && dynamicForms.map(item => (
          <NavItem
            key={item.id}
            theme={theme}
            $active={currentPath === item.path}
            onClick={() => handleNavigation(item.path)}
            title={item.label}
          >
            <NavIcon>{renderFormIcon(item.icon, 18)}</NavIcon>
            <NavLabel $visible={isExpanded} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.label}
            </NavLabel>
          </NavItem>
        ))}
      </>
    );
  };

  return (
    <LayoutContainer theme={theme}>
      <MobileOverlay
        $show={isMobileSidebarOpen}
        onClick={() => setIsMobileSidebarOpen(false)}
      />

      <Sidebar
        theme={theme}
        $isExpanded={isExpanded}
        $isHovered={isHovered}
        $isPinned={isPinned}
        $isMobileOpen={isMobileSidebarOpen}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <SidebarTitle>aQRate Portal</SidebarTitle>
        <SidebarNav theme={theme}>
          {sidebarSections.map(renderSidebarSection)}
          {!isSuperAdmin() && renderDynamicFormsSection()}
        </SidebarNav>

        {isExpanded && (
          <div style={{ flexShrink: 0, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <PinButton
              theme={theme}
              $pinned={isPinned}
              $visible={true}
              onClick={handleTogglePin}
              title={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
              style={{ width: '100%', margin: 0, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '12px', borderRadius: 0, height: 'auto' }}
            >
              {isPinned ? <PinOff size={16} /> : <Pin size={16} />}
              <span style={{ fontSize: '13px' }}>{isPinned ? 'Unpin Menu' : 'Pin Menu'}</span>
            </PinButton>
            <div style={{
              padding: '4px 16px 10px',
              textAlign: 'center',
            }}>
              <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.35)', letterSpacing: '0.5px' }}>
                v{import.meta.env.VITE_VERSION || '1.0.0'}
                {import.meta.env.VITE_ENVIRONMENT && import.meta.env.VITE_ENVIRONMENT !== 'production' && (
                  <span style={{ marginLeft: '6px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(255, 180, 50, 0.15)', color: 'rgba(255, 180, 50, 0.7)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase' }}>
                    {import.meta.env.VITE_ENVIRONMENT}
                  </span>
                )}
              </span>
            </div>
          </div>
        )}
      </Sidebar>

      <MainContent $sidebarWidth={sidebarWidth} $isPinned={isPinned}>
        {user?.isReadOnlyDemo && (
          <ReadOnlyBanner theme={theme}>
            <Eye size={14} />
            You&apos;re exploring a read-only demo — changes are disabled.
          </ReadOnlyBanner>
        )}
        <TopBar theme={theme}>
          <TopBarLeft theme={theme}>
            <MobileMenuButton
              theme={theme}
              onClick={() => setIsMobileSidebarOpen(true)}
            >
              <Menu size={20} />
            </MobileMenuButton>
            <TopBarLogo src="/img/aqrate-logo-icon.png" alt="aQRate" />
            {pageTitle && <PageTitle theme={theme}>{pageTitle}</PageTitle>}
            {user?.isDemoOrg && <DemoBadge theme={theme}>Demo</DemoBadge>}
            <DevAccountSwitcher />
          </TopBarLeft>

          <UserMenu theme={theme}>
            {DEV_TOOLS_ENABLED && (
              <TopBarIconButton
                theme={theme}
                onClick={() => setIsBugModalOpen(true)}
                title="Report a Bug (QA)"
              >
                <Bug size={16} />
              </TopBarIconButton>
            )}

            <TopBarIconButton
              theme={theme}
              onClick={() => setIsFaqModalOpen(true)}
              title="Help & FAQs"
            >
              <BadgeHelp size={16} />
            </TopBarIconButton>

            <UserAvatarContainer
              theme={theme}
              onClick={handleProfileMenuToggle}
              data-profile-menu
            >
              <Avatar src={user?.profilePicture} name={user?.fullName || 'User'} size="md" />

              <div style={{ display: window.innerWidth > 640 ? 'block' : 'none' }}>
                <ThemedText type="defaultSemiBold">
                  {user?.fullName || 'User'}
                </ThemedText>
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  {user?.role || 'User'}
                </ThemedText>
              </div>

              <span style={{ display: 'flex', alignItems: 'center', transform: isProfileMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                <ChevronDown size={14} />
              </span>

              <ProfileDropdown
                theme={theme}
                $isOpen={isProfileMenuOpen}
                data-profile-menu
              >
                <DropdownUserHeader theme={theme}>
                  <div style={{ fontWeight: 600, color: theme.colors.text, marginBottom: 2 }}>
                    {user?.fullName || 'User'}
                  </div>
                  <div style={{ fontSize: '13px', color: theme.colors.muted }}>
                    {user?.email || ''}
                  </div>
                </DropdownUserHeader>
                <DropdownItem theme={theme} onClick={handleProfileClick}>
                  <Settings size={16} />
                  Settings
                </DropdownItem>
                {!isSuperAdmin() && (
                  <DropdownItem theme={theme} onClick={() => handleNavigation('/tasks/new')}>
                    <AlertTriangle size={16} />
                    Report a Problem
                  </DropdownItem>
                )}
                <DropdownDivider theme={theme} />
                <DropdownItem theme={theme} onClick={handleLogout}>
                  <LogOut size={16} />
                  Logout
                </DropdownItem>
              </ProfileDropdown>
            </UserAvatarContainer>
          </UserMenu>
        </TopBar>

        <ContentArea key={location.pathname} theme={theme}>
          {children}
        </ContentArea>
      </MainContent>

      <FAQModal
        isOpen={isFaqModalOpen}
        onClose={() => setIsFaqModalOpen(false)}
        organizationId={user?.organizationId}
      />

      {DEV_TOOLS_ENABLED && (
        <BugReportModal
          isOpen={isBugModalOpen}
          onClose={() => setIsBugModalOpen(false)}
        />
      )}
    </LayoutContainer>
  );
};
